import { datasets, totalCells } from "./data";

export type RegistryStatus =
  | "verified"
  | "verified-with-boundary"
  | "corrected-and-verified";

export type FieldStatus =
  | "verified"
  | "verified-with-boundary"
  | "source-conflict"
  | "not-deposited"
  | "not-verified";

export type RegistryEvidence = {
  label: string;
  url: string;
  role: string;
  checkedAt: string;
};

export type ResearchRecord = {
  id: string;
  recordVersion: string;
  portalRecord: string;
  title: string;
  citation: string;
  doi: string;
  organism: string;
  organismIdentifier: string;
  organismIdentifierUrl: string;
  strain: string;
  strainAliases?: string[];
  secondaryComparators?: string[];
  accessions: string[];
  measurementTechnique: string;
  cellCount: number;
  cellCountBasis: string;
  sampleContext: string;
  reference: string;
  expressionLayer: string;
  embeddingLayer: string;
  portalProcessing: string;
  recordStatus: RegistryStatus;
  articleLicence: string;
  dataLicence: string;
  codeAvailability: string;
  codeLicence: string;
  portalMetadataLicence: string;
  lastVerified: string;
  knownBoundary: string;
  counts: {
    displayed: number;
    displayedBasis: string;
    preDoublet?: number;
    doubletsRemoved?: number;
    finalSinglets?: number;
  };
  fieldStatus: {
    identity: FieldStatus;
    biology: FieldStatus;
    counts: FieldStatus;
    reference: FieldStatus;
    expression: FieldStatus;
    coordinates: FieldStatus;
    rights: FieldStatus;
  };
  primaryEvidence: RegistryEvidence[];
};

export const researchRecords: ResearchRecord[] = [
  {
    id: "brg-2021-bsf",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/prjeb41744/",
    title:
      "Bloodstream differentiation time course and ZC3H20 knockout single-cell atlas",
    citation:
      "Briggs EM et al. Nature Communications 12, 5268 (2021).",
    doi: "https://doi.org/10.1038/s41467-021-25607-2",
    organism: "Trypanosoma brucei brucei",
    organismIdentifier: "NCBI Taxonomy:5702",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5702",
    strain: "EATRO 1125 AnTa1.1 90:13 (paper spelling)",
    strainAliases: ["EATRO 1125 AnTat1.1 (common alias)"],
    accessions: ["ENA PRJEB41744", "Zenodo 5163554"],
    measurementTechnique:
      "Chromium Single Cell 3′ GEM, Library & Gel Bead Kit v3",
    cellCount: 10894,
    cellCountBasis:
      "8,599 WT plus 2,295 ZC3H20-KO cells in author-filtered matrices",
    sampleContext:
      "In-vitro bloodstream-form differentiation time course and ZC3H20 knockout",
    reference:
      "TREU927 nuclear genome, TriTrypDB release 50; Lister 427 maxi-circle M94286.1",
    expressionLayer:
      "TrypCell-pinned sparse conversion of author-deposited filtered integer transcript counts",
    embeddingLayer: "Author UMAP and PCA",
    portalProcessing:
      "The three Zenodo count tables are integrity-checked and converted to versioned sparse same-origin blocks; no normalization or imputation is applied and no author service is queried at runtime. Coordinate rounding does not alter author labels. Optional cell-level Kruskal–Wallis/BH, Cliff’s delta and sample aggregations are portal-derived exploratory outputs and are exported separately.",
    recordStatus: "verified",
    articleLicence: "Article: CC BY 4.0",
    dataLicence: "Zenodo 5163554 archive: CC BY 4.0",
    codeAvailability:
      "Analysis code is included in the Zenodo 5163554 archive",
    codeLicence:
      "Zenodo 5163554 applies CC BY 4.0 to the deposited archive",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-29",
    knownBoundary:
      "ENA exposed submitted BAM and BAI files, but no FASTQ or SRA FTP links, on the audit date. Zenodo 5163554 records CC BY 4.0 for its deposited archive; file-level evidence has not yet established that the same grant covers coordinates and metadata decoded from the separately hosted author H5AD service.",
    counts: {
      displayed: 10894,
      displayedBasis:
        "8,599 author-filtered WT cells plus 2,295 author-filtered ZC3H20-KO cells",
      finalSinglets: 10894,
    },
    fieldStatus: {
      identity: "verified",
      biology: "verified-with-boundary",
      counts: "verified",
      reference: "verified",
      expression: "verified",
      coordinates: "verified",
      rights: "verified-with-boundary",
    },
    primaryEvidence: [
      {
        label: "Version-of-record paper",
        url: "https://www.nature.com/articles/s41467-021-25607-2",
        role: "Study design, strain, chemistry, reference and author cell-count boundary",
        checkedAt: "2026-07-27",
      },
      {
        label: "Zenodo 5163554",
        url: "https://zenodo.org/records/5163554",
        role: "Processed matrices, analysis archive, checksum and CC BY 4.0 licence",
        checkedAt: "2026-07-27",
      },
      {
        label: "ENA PRJEB41744",
        url: "https://www.ebi.ac.uk/ena/browser/view/PRJEB41744",
        role: "Raw sequencing accession and submitted-file manifest",
        checkedAt: "2026-07-27",
      },
    ],
  },
  {
    id: "how-2022-tsetse",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/erp132258/",
    title:
      "Tsetse midgut, proventriculus and salivary-gland single-cell atlas",
    citation: "Howick VM et al. PLOS Pathogens 18, e1010346 (2022).",
    doi: "https://doi.org/10.1371/journal.ppat.1010346",
    organism: "Trypanosoma brucei brucei",
    organismIdentifier: "NCBI Taxonomy:5702",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5702",
    strain:
      "J10 (MCRO/ZM/73/J10) and 1738 (MOVS/KE/70/EATRO 1738)",
    accessions: ["ENA ERP132258 / PRJEB47937", "Zenodo 6047732 v1.0"],
    measurementTechnique:
      "Modified Smart-seq2 with Nextera XT library preparation",
    cellCount: 388,
    cellCountBasis:
      "Fly-derived cells passing author QC; 46 in-vitro validation single cells are excluded",
    sampleContext:
      "Tsetse midgut, proventriculus and salivary gland across days 21, 24 and 40",
    reference:
      "T. b. brucei 927 genome; HISAT2 2.1.0; HTSeq 0.7.1",
    expressionLayer: "Author-deposited Smart-seq2 integer read counts",
    embeddingLayer: "Author fig1 UMAP coordinates",
    portalProcessing:
      "Static browser conversion and per-axis display scaling do not rerun UMAP, clustering, normalization or imputation. Optional cell-level statistics are separately labelled as portal-derived exploratory outputs.",
    recordStatus: "verified-with-boundary",
    articleLicence: "Article: CC BY 4.0",
    dataLicence:
      "Zenodo metadata uses “other-open”; the associated article licence is not applied to the data files",
    codeAvailability:
      "Analysis code and rendered outputs are included in the Zenodo v1.0 archive",
    codeLicence:
      "Zenodo record uses the non-standard identifier “other-open”; inspect the source record before reuse",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-27",
    knownBoundary:
      "DP is a FACS sorting group, not a strain. C5 is predominantly J10 and C6 predominantly 1738, so stage and strain effects may be confounded. Poor 1738/J10 VSG matching to the 927 reference can underestimate VSG abundance. 387 of 388 processed cells match current ENA aliases; 29784_4#45 remains unresolved.",
    counts: {
      displayed: 388,
      displayedBasis:
        "78 midgut + 34 proventriculus + 276 salivary-gland cells passing author QC; excludes 46 in-vitro validation cells",
      finalSinglets: 388,
    },
    fieldStatus: {
      identity: "verified",
      biology: "verified-with-boundary",
      counts: "verified",
      reference: "verified",
      expression: "verified",
      coordinates: "verified",
      rights: "verified-with-boundary",
    },
    primaryEvidence: [
      {
        label: "Version-of-record paper",
        url: "https://journals.plos.org/plospathogens/article?id=10.1371/journal.ppat.1010346",
        role: "Strains, cell counts, Smart-seq2 workflow, mapping pipeline and interpretation limits",
        checkedAt: "2026-07-27",
      },
      {
        label: "Zenodo 6047732",
        url: "https://zenodo.org/records/6047732",
        role: "Author matrices, metadata, embeddings, code and repository licence identifier",
        checkedAt: "2026-07-27",
      },
      {
        label: "ENA ERP132258",
        url: "https://www.ebi.ac.uk/ena/browser/view/ERP132258",
        role: "Raw sequencing accession and run aliases",
        checkedAt: "2026-07-27",
      },
    ],
  },
  {
    id: "brg-2023-replicative",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/prjeb58781/",
    title:
      "Bloodstream-form and procyclic-form cell-cycle single-cell atlas",
    citation: "Briggs EM et al. eLife 12:e86325 (2023).",
    doi: "https://doi.org/10.7554/eLife.86325",
    organism: "Trypanosoma brucei brucei",
    organismIdentifier: "NCBI Taxonomy:5702",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5702",
    strain:
      "T. brucei Lister 427-derived bloodstream- and procyclic-form lines; subclone not specified in this portal passport",
    accessions: ["ENA PRJEB58781", "Zenodo 7508131"],
    measurementTechnique: "Chromium Single Cell 3′ v3.1",
    cellCount: 12006,
    cellCountBasis:
      "4,366 bloodstream-form plus 7,640 procyclic-form rows in author phase tables",
    sampleContext:
      "Fresh and frozen replicative bloodstream and procyclic forms",
    reference:
      "Author mapping: T. brucei WT427 2018 with 3′ UTRs extended by 2,500 bp; L. major Friedlin co-reference used to remove L. major cells and mixed-species multiplets; Cell Ranger 7",
    expressionLayer:
      "TrypCell-pinned signed values from author-deposited BSF_normcounts.csv (1,652 features) and PCF_normcounts.csv (1,738 features)",
    embeddingLayer: "Separate author UMAP and MNN spaces for BSF and PCF",
    portalProcessing:
      "Author-deposited CSV values are integrity-checked and converted to versioned sparse same-origin blocks; positive and negative values are preserved. No alignment, normalization, phase reassignment, recomputation or runtime author-service request is applied.",
    recordStatus: "verified",
    articleLicence: "Article: CC BY 4.0",
    dataLicence: "Zenodo record: CC BY 4.0",
    codeAvailability:
      "R Markdown, rendered analyses and processed objects are included in Zenodo",
    codeLicence: "Zenodo 7508131: CC BY 4.0",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-29",
    knownBoundary:
      "BSF and PCF coordinates were calculated separately and must not be treated as one shared space. The deposited expression files contain only author-selected variable-feature subsets and include signed values; genes absent from those CSV files are reported unavailable rather than inferred as zero. Values are not labelled as raw UMI counts.",
    counts: {
      displayed: 12006,
      displayedBasis:
        "4,366 BSF (2,767 fresh + 1,599 frozen) plus 7,640 PCF (3,305 fresh + 4,335 frozen)",
      finalSinglets: 12006,
    },
    fieldStatus: {
      identity: "verified",
      biology: "verified-with-boundary",
      counts: "verified",
      reference: "verified",
      expression: "verified-with-boundary",
      coordinates: "verified-with-boundary",
      rights: "verified",
    },
    primaryEvidence: [
      {
        label: "Version-of-record paper",
        url: "https://elifesciences.org/articles/86325",
        role: "Study design, strain lineage, chemistry, mapping reference and counts",
        checkedAt: "2026-07-27",
      },
      {
        label: "Zenodo 7508131",
        url: "https://zenodo.org/records/7508131",
        role: "Author processed files, analysis code and CC BY 4.0 licence",
        checkedAt: "2026-07-27",
      },
      {
        label: "ENA PRJEB58781",
        url: "https://www.ebi.ac.uk/ena/browser/view/PRJEB58781",
        role: "Raw sequencing accession",
        checkedAt: "2026-07-27",
      },
    ],
  },
  {
    id: "reu-2023-skin",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/gse174198/",
    title:
      "Early infection and tissue adaptation in an artificial human skin model",
    citation:
      "Reuter C et al. Nature Communications 14, 7660 (2023).",
    doi: "https://doi.org/10.1038/s41467-023-43437-2",
    organism: "Trypanosoma brucei (repository organism label)",
    organismIdentifier: "NCBI Taxonomy:5691",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5691",
    strain:
      "Pleomorphic T. brucei EATRO 1125 AnTat 1.1 (main single-parasite RNA-seq study)",
    secondaryComparators: [
      "Monomorphic Lister 427 MITat 1.2 13-90 used for syringe-injection comparison",
    ],
    accessions: ["GEO GSE174198", "BioProject PRJNA728721", "SRA SRP319235"],
    measurementTechnique:
      "SMART-Seq v4 Ultra Low Input RNA Kit with Nextera XT",
    cellCount: 170,
    cellCountBasis:
      "139 metacyclic/skin time-course cells plus 31 bloodstream-form cells passing author QC",
    sampleContext:
      "Metacyclic forms, 4 h, 12 h, 24 h and 7 d skin time points, plus bloodstream forms",
    reference:
      "TREU927 release 48 plus ERCC sequences; RNASTAR 2.6.1b; featureCounts 2.0.1",
    expressionLayer:
      "139-cell normalized MCF/skin table and separate 31-cell integer BSF count table",
    embeddingLayer:
      "No reusable author PCA coordinates deposited; portal provides a non-analytical sample index",
    portalProcessing:
      "Source-separated display and selection only; no PCA tracing, reconstructed embedding or matrix merging",
    recordStatus: "corrected-and-verified",
    articleLicence: "Article: CC BY 4.0",
    dataLicence:
      "No separate data-file licence was recorded in the GEO audit; repository terms and source authorship remain controlling",
    codeAvailability:
      "The paper mentions GitHub, but the official article pages expose no verifiable repository URL",
    codeLicence:
      "Not verified because an official code repository URL is unresolved",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-27",
    knownBoundary:
      "The 139-cell MCF/skin normalized table and 31-cell BSF integer-count table use different units and are never merged. Reusable author PCA coordinates were not deposited; the portal sample index is explicitly non-analytical. Methods specify the SMART-Seq v4 kit, while the Figure 4 caption calls the workflow Smart-seq2/Nextera.",
    counts: {
      displayed: 170,
      displayedBasis:
        "139 MCF/skin cells after three paper-specified exclusions plus 31 BSF cells",
      finalSinglets: 170,
    },
    fieldStatus: {
      identity: "verified",
      biology: "verified-with-boundary",
      counts: "verified",
      reference: "verified",
      expression: "verified-with-boundary",
      coordinates: "not-deposited",
      rights: "verified-with-boundary",
    },
    primaryEvidence: [
      {
        label: "Version-of-record paper",
        url: "https://www.nature.com/articles/s41467-023-43437-2",
        role: "Article number, strains, workflow, cell counts, reference and PCA boundary",
        checkedAt: "2026-07-27",
      },
      {
        label: "GEO GSE174198",
        url: "https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=GSE174198",
        role: "Deposited expression tables, sample metadata and raw-data links",
        checkedAt: "2026-07-27",
      },
    ],
  },
  {
    id: "lai-2026-cruzi",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/e-mtab-14406/",
    title:
      "In-vitro Trypanosoma cruzi Silvio X10/7 A1 lifecycle single-cell atlas",
    citation:
      "Laidlaw RF et al. Nature Communications 17, 6801 (2026).",
    doi: "https://doi.org/10.1038/s41467-026-73098-w",
    organism: "Trypanosoma cruzi",
    organismIdentifier: "NCBI Taxonomy:5693",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5693",
    strain: "Silvio X10/7 A1 (MHOM/BR/78/Silvio lineage)",
    accessions: [
      "BioStudies E-MTAB-14406",
      "ENA ERP163729",
      "Bulk RNA-seq E-MTAB-14400",
      "UTR annotation Zenodo 14229160",
      "Code archive Zenodo 19498217",
    ],
    measurementTechnique:
      "Chromium Next GEM Single Cell 3′ Reagent Kit v3.1",
    cellCount: 31065,
    cellCountBasis: "Valid cells retained after author quality control",
    sampleContext:
      "Epimastigote, metacyclic, trypomastigote and amastigote lifecycle stages",
    reference:
      "Author mapping: Dm28c 2018 plus maxicircle with 3′ UTRs extended by 2,500 bp; GRCh38 was added for AMA6/24, EP/AMA120 and MIX but not SE/MT or CDT; Cell Ranger 6.0.0. The related UTR annotation asset uses TriTrypDB release 68.",
    expressionLayer:
      "Author CELL×GENE active signed scaled matrix; not raw counts",
    embeddingLayer: "Author UMAP and PCA",
    portalProcessing:
      "CELL×GENE response decoding and display-coordinate rounding only; no rescaling of expression or reanalysis",
    recordStatus: "verified-with-boundary",
    articleLicence: "Article: CC BY 4.0",
    dataLicence:
      "E-MTAB-14406 data-file licence not independently verified; related UTR asset Zenodo 14229160 is CC BY 4.0",
    codeAvailability:
      "Author repository: github.com/No2Ross/TcruziAtlas; immutable code archive: Zenodo 19498217",
    codeLicence:
      "A code-specific licence has not yet been independently verified",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-27",
    knownBoundary:
      "BioStudies exposes IDF/SDRF metadata and raw-read links but no processed count matrix. The author CELL×GENE active matrix contains signed scaled values, not raw counts. Silvio X10/7 A1 is the biological strain; Dm28c 2018 is the mapping reference.",
    counts: {
      displayed: 31065,
      displayedBasis: "Valid cells retained after author quality control",
    },
    fieldStatus: {
      identity: "verified",
      biology: "verified-with-boundary",
      counts: "verified",
      reference: "verified-with-boundary",
      expression: "verified-with-boundary",
      coordinates: "verified",
      rights: "verified-with-boundary",
    },
    primaryEvidence: [
      {
        label: "Version-of-record paper",
        url: "https://www.nature.com/articles/s41467-026-73098-w",
        role: "Biological strain, study design, cell count, mapping workflow and data availability",
        checkedAt: "2026-07-27",
      },
      {
        label: "BioStudies E-MTAB-14406",
        url: "https://www.ebi.ac.uk/biostudies/arrayexpress/studies/E-MTAB-14406",
        role: "Single-cell sample metadata and raw-read accessions",
        checkedAt: "2026-07-27",
      },
      {
        label: "Zenodo 14229160",
        url: "https://zenodo.org/records/14229160",
        role: "Release-68 Dm28c UTR annotation asset, checksum and CC BY 4.0 licence",
        checkedAt: "2026-07-27",
      },
      {
        label: "Author analysis repository",
        url: "https://github.com/No2Ross/TcruziAtlas",
        role: "Public analysis code named by the authors",
        checkedAt: "2026-07-27",
      },
    ],
  },
  {
    id: "inc-2026-cruzi",
    recordVersion: "2026.07.29.1",
    portalRecord: "/datasets/prjna1200704/",
    title:
      "Mammalian-stage Trypanosoma cruzi Dm28c single-cell transcriptomics",
    citation:
      "Inchausti L et al. eLife 14, RP105822; Version of Record published 7 April 2026.",
    doi: "https://doi.org/10.7554/eLife.105822",
    organism: "Trypanosoma cruzi",
    organismIdentifier: "NCBI Taxonomy:5693",
    organismIdentifierUrl:
      "https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=5693",
    strain: "DM28c",
    accessions: [
      "BioProject PRJNA1200704",
      "SRA SRR31781067",
      "Zenodo 18760090",
    ],
    measurementTechnique:
      "Chromium Next GEM Single Cell 3′ v3.1 after methanol fixation",
    cellCount: 3151,
    cellCountBasis:
      "Final retained singlets after removing 41 doublets from 3,192 post-QC cells",
    sampleContext:
      "Amastigotes and cell-derived trypomastigotes obtained from infected rat H9c2 myoblasts (ATCC CRL-1446), plus transitional cells",
    reference:
      "Dm28c 2018 genome, TriTrypDB release 62, combined with Dm28c maxicircle kDNA; 11,362 3′ UTRs annotated with peaks2UTR; kallisto bustools 0.51.1",
    expressionLayer:
      "Two author-deposited real-valued kallisto bustools technical matrices",
    embeddingLayer:
      "Final author UMAP coordinates and post-doublet cell metadata are not deposited",
    portalProcessing:
      "Source and workflow indexing only; no reconstructed atlas or inferred final labels",
    recordStatus: "verified-with-boundary",
    articleLicence: "eLife article: CC BY",
    dataLicence: "Zenodo record: CC BY 4.0",
    codeAvailability:
      "Public GitHub workflow audited at revision 0396b4d7b3e3089b302997166e76dc69b0423d1e",
    codeLicence:
      "A code-specific licence has not yet been independently verified",
    portalMetadataLicence:
      "Not yet assigned; no reuse licence is implied for portal-curated metadata",
    lastVerified: "2026-07-27",
    knownBoundary:
      "Final post-doublet metadata, cluster counts, UMAP and Seurat/H5AD object are not deposited. The version-of-record Methods and current public R script use different nUMI and nGene thresholds. SRA “isolation source: Axenic culture” is a repository field and does not replace the infected-H9c2 experimental source.",
    counts: {
      displayed: 3151,
      displayedBasis:
        "Final singlets retained for downstream analysis; no cell-level atlas is reconstructed because final metadata are not deposited",
      preDoublet: 3192,
      doubletsRemoved: 41,
      finalSinglets: 3151,
    },
    fieldStatus: {
      identity: "verified-with-boundary",
      biology: "verified-with-boundary",
      counts: "verified-with-boundary",
      reference: "verified",
      expression: "verified-with-boundary",
      coordinates: "not-deposited",
      rights: "verified-with-boundary",
    },
    primaryEvidence: [
      {
        label: "eLife Version of Record",
        url: "https://elifesciences.org/articles/105822",
        role: "Study design, publication status, cell-count boundary and analysis workflow",
        checkedAt: "2026-07-27",
      },
      {
        label: "Zenodo 18760090",
        url: "https://zenodo.org/records/18760090",
        role: "Deposited technical matrices, supporting files and CC BY 4.0 licence",
        checkedAt: "2026-07-27",
      },
      {
        label: "BioProject PRJNA1200704",
        url: "https://www.ncbi.nlm.nih.gov/bioproject/PRJNA1200704",
        role: "Raw sequencing and BioSample provenance",
        checkedAt: "2026-07-27",
      },
      {
        label: "Public analysis workflow",
        url: "https://github.com/bioinfo-iibce/scRNAseq_Tcruzi",
        role: "Code revision and paper-versus-script threshold audit",
        checkedAt: "2026-07-27",
      },
    ],
  },
];

export const researchRegistry = {
  schemaVersion: "1.1.0",
  registryVersion: "2026.07.29.1",
  releasedAt: "2026-07-29",
  scope:
    "Public parasite single-cell transcriptomics records curated for TrypCell Atlas",
  alignedWith: [
    {
      name: "Schema.org Dataset",
      url: "https://schema.org/Dataset",
      status:
        "basic JSON-LD mapping implemented; stable identifiers and file-level distributions remain incomplete",
    },
    {
      name: "Bioschemas Dataset 1.0-RELEASE",
      url: "https://bioschemas.org/profiles/Dataset/1.0-RELEASE",
      status:
        "mapping in progress; the current registry is not claimed to be profile-conformant",
    },
    {
      name: "FAIR Guiding Principles",
      url: "https://www.go-fair.org/fair-principles/",
      status:
        "FAIR-informed implementation target; no blanket FAIR-conformance claim",
    },
  ],
  totals: {
    studies: datasets.length,
    catalogueCellEntries: totalCells,
  },
  portalDerivedAnalysis: {
    status: "implemented-and-separately-labelled",
    availableFor: ["brg-2021-bsf", "how-2022-tsetse"],
    analysisRecordFormatVersion: "1.0",
    methods: [
      "Grouped detection rates, raw-count means and quartiles",
      "Kruskal–Wallis H with pooled ranks and tie correction",
      "Benjamini–Hochberg adjustment across the currently compared genes",
      "Ordinal epsilon-squared and descriptive Cliff’s delta",
      "Raw-count aggregation by author Sample ID",
    ],
    inputBoundary:
      "Calculations use unchanged author-deposited raw-count columns and current user-selected cells or filters.",
    limitation:
      "Individual cells are not independent biological replicates. These outputs are exploratory, are not author results and do not replace replicate-aware pseudobulk or differential-expression analysis.",
  },
  records: researchRecords,
  interpretation: {
    cellCount:
      "Cell totals are catalogue entries with study-specific counting boundaries, not a harmonised pooled cohort.",
    licence:
      "Article, data and code licences are separate. An article licence is never assumed to license deposited data files.",
    processing:
      "Author results, portal format conversion and portal reanalysis are separate provenance classes.",
    portalAnalysis:
      "The portal does not recompute atlas embeddings or clusters. It does provide separately labelled exploratory cell-level statistics for two raw-count atlases.",
    reuse:
      "Users must cite the primary paper and repository and inspect the source record for current reuse terms.",
  },
} as const;

export const datasetById = new Map(
  datasets.map((dataset) => [dataset.id, dataset]),
);
